/**
 * Calculation utilities for Analytics component
 */

import { ChartDataset } from '../_interfaces/analytics.interfaces';
import { InventoryProjectionRow } from '../_interfaces/analytics.interfaces';

/**
 * Calculate total from a dataset's data array
 */
export function calculateDatasetTotal(dataset: ChartDataset | undefined): number {
  if (!dataset || !dataset.data || !Array.isArray(dataset.data)) {
    return 0;
  }
  return (dataset.data as number[]).reduce((sum, value) => sum + (value || 0), 0);
}

/**
 * Calculate total from a number array
 */
export function calculateArrayTotal(data: number[] | undefined): number {
  if (!data || data.length === 0) {
    return 0;
  }
  return data.reduce((sum, value) => sum + (value || 0), 0);
}

/**
 * Get value at index from a dataset's data array
 */
export function getDatasetValue(dataset: ChartDataset | undefined, index: number): number {
  if (!dataset || !dataset.data || !Array.isArray(dataset.data)) {
    return 0;
  }
  if (index < 0 || index >= dataset.data.length) {
    return 0;
  }
  return (dataset.data[index] as number) || 0;
}

/**
 * Get value at index from a number array
 */
export function getArrayValue(data: number[] | undefined, index: number): number {
  if (!data || index < 0 || index >= data.length) {
    return 0;
  }
  return data[index] || 0;
}

/**
 * Calculate total from first dataset in array (helper for bar charts)
 */
export function calculateFirstDatasetTotal(datasets: ChartDataset[]): number {
  if (!datasets || datasets.length === 0 || !datasets[0]) {
    return 0;
  }
  return calculateDatasetTotal(datasets[0]);
}

/**
 * Get value for a specific product (dataset) and label index
 */
export function getProductValue(
  datasets: ChartDataset[],
  productIndex: number,
  labelIndex: number
): number {
  if (!datasets || datasets.length === 0 || productIndex < 0 || productIndex >= datasets.length) {
    return 0;
  }
  return getDatasetValue(datasets[productIndex], labelIndex);
}

/**
 * Calculate total for a specific product (dataset)
 */
export function getProductTotal(
  datasets: ChartDataset[],
  productIndex: number
): number {
  if (!datasets || datasets.length === 0 || productIndex < 0 || productIndex >= datasets.length) {
    return 0;
  }
  return calculateDatasetTotal(datasets[productIndex]);
}

/**
 * Calculate forecast line chart total (returns number for proper formatting)
 */
export function calculateForecastLineTotal(dataset: ChartDataset): number {
  if (!dataset || !dataset.data || !Array.isArray(dataset.data)) {
    return 0;
  }
  const total = (dataset.data as number[]).reduce((sum, value) => sum + (value || 0), 0);
  // Round to 2 decimal places to avoid floating point precision errors
  return Math.round(total * 100) / 100;
}

/**
 * Calculate inventory projection current total
 */
export function calculateInventoryProjectionCurrentTotal(table: InventoryProjectionRow[]): number {
  if (!table || table.length === 0) {
    return 0;
  }
  const total = table.reduce((sum, row) => sum + (row.current || 0), 0);
  // Round to 2 decimal places to avoid floating point precision errors
  return Math.round(total * 100) / 100;
}

/**
 * Calculate inventory projection end total
 */
export function calculateInventoryProjectionEndTotal(table: InventoryProjectionRow[]): number {
  if (!table || table.length === 0) {
    return 0;
  }
  const total = table.reduce((sum, row) => sum + (row.projectedEnd || 0), 0);
  // Round to 2 decimal places to avoid floating point precision errors
  return Math.round(total * 100) / 100;
}

